import { Button, Heading, Text } from "@medusajs/ui"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { logout, useMe } from "@/lib/api"
import { MeProvider, useCan } from "@/lib/permissions"
import type { Permission } from "@/lib/types"
import { TwoFactorPrompt, TwoFactorSetupGate } from "./two-factor"

function FullPage({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-ui-bg-subtle p-4">{children}</div>
}

/** Signed in + has Terify Admin access, or bounce to /login / a "no access" screen. */
export function RequireSession() {
  const { data: me, isLoading, error } = useMe()
  const location = useLocation()

  if (isLoading) {
    return (
      <FullPage>
        <Text size="small" className="text-ui-fg-muted">
          Loading…
        </Text>
      </FullPage>
    )
  }
  if (error) {
    return (
      <FullPage>
        <Text>Couldn't reach the store backend. Try again in a moment.</Text>
      </FullPage>
    )
  }
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />

  if (me.two_factor?.enabled && !me.two_factor.verified) return <TwoFactorPrompt email={me.user.email} method={me.two_factor.method ?? "app"} phoneHint={me.two_factor.phone_hint ?? null} />
  if (me.staff && me.two_factor?.required && !me.two_factor.enabled) return <TwoFactorSetupGate email={me.user.email} smsAvailable={!!me.two_factor.sms_available} />

  if (!me.staff) {
    return (
      <FullPage>
        <div className="flex max-w-sm flex-col items-center gap-y-3 text-center">
          <Heading>No access yet</Heading>
          <Text size="small" className="text-ui-fg-subtle">
            You're signed in as {me.user.email}, but your account doesn't have access to this store's admin. Ask the store owner to add you under Settings → Users and permissions.
          </Text>
          <Button variant="secondary" size="small" onClick={() => logout()}>
            Sign out
          </Button>
        </div>
      </FullPage>
    )
  }

  return (
    <MeProvider me={me}>
      <Outlet />
    </MeProvider>
  )
}

export function RequirePermission({ perm, children }: { perm: Permission; children: React.ReactNode }) {
  const can = useCan()
  if (can(perm)) return <>{children}</>
  return (
    <div className="flex flex-col items-center gap-y-2 py-24 text-center">
      <Heading level="h2">You don't have access to this page</Heading>
      <Text size="small" className="text-ui-fg-subtle">
        It needs the “{perm}” permission. Ask the store owner if you need it.
      </Text>
    </div>
  )
}

/** Home needs analytics:read; people without it land on their first allowed page instead. */
export function HomeRedirect({ children }: { children: React.ReactNode }) {
  const can = useCan()
  if (can("analytics:read")) return <>{children}</>
  const first = (
    [
      ["orders:read", "/orders"],
      ["products:read", "/products"],
      ["customers:read", "/customers"],
      ["staff:read", "/settings/users"],
    ] as [Permission, string][]
  ).find(([p]) => can(p))
  return first ? <Navigate to={first[1]} replace /> : <RequirePermission perm="analytics:read">{null}</RequirePermission>
}
